'use client';
import { InlineScript } from 'components/atoms/InlineScript';
import React, {
  ComponentType,
  createContext,
  ReactElement,
  ReactNode,
  useContext,
} from 'react';
import { first } from 'utils/array';

export const ServerRenderContext = createContext(true);

export type BlackBoxProps = {
  children: ReactNode;
  className?: string;
};

export function BlackBox({ children, className }: BlackBoxProps): JSX.Element {
  return (
    <ServerRenderContext.Provider value={true}>
      <div data-hydration="none" className={className}>
        {children}
      </div>
    </ServerRenderContext.Provider>
  );
}

export type DehydrateProps<P> = P & {
  component: { [name: string]: ComponentType<P> };
  children?: ReactElement | ReactElement[];
};

/**
 * Hydrate the component on the client
 */
export function Dehydrate<P extends object>({
  children,
  component,
  ...props
}: DehydrateProps<P>): JSX.Element {
  const ssr = useContext(ServerRenderContext);

  const delegate = first(Object.entries(component));
  if (!delegate) {
    throw new Error('no component configured');
  }
  const [name, Component] = delegate;
  const DelegatedComponent = Component as ComponentType<
    P & { children?: ReactNode }
  >;

  const nodes = React.Children.toArray(children)
    .filter((node) => React.isValidElement(node))
    .map((node) => {
      if (React.isValidElement<BlackBoxProps>(node) && node.type === BlackBox) {
        const props = node.props;
        return {
          props: { className: props.className },
          element: props.children,
        };
      }
      return {
        props: {},
        element: node,
      };
    });

  // All props will be serialized to JSON, so emulate this in development
  // This will catch props that are not serializable
  let p: P = props as P;
  if (typeof window === 'undefined') {
    try {
      p = JSON.parse(JSON.stringify(props));
    } catch (e) {
      throw new Error(
        `Props for <Dehydrate component={{${Component.name}}}/> should be serializable: ${e}`,
      );
    }
  }

  return (
    <div data-hydration={ssr ? 'false' : 'skip'}>
      <ServerRenderContext.Provider value={false}>
        <DelegatedComponent {...p}>
          {nodes.map((child, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <BlackBox key={i} {...child.props}>
              {child.element}
            </BlackBox>
          ))}
        </DelegatedComponent>
      </ServerRenderContext.Provider>
      {ssr && (
        <InlineScript type="application/x-component+json" data-name={name}>
          {JSON.stringify({
            ...props,
            children: nodes.map((child) => child.props),
          })}
        </InlineScript>
      )}
    </div>
  );
}
